import * as React from 'react';
import { connectSearchBox } from 'react-instantsearch-core';
import { Configure, InstantSearch } from 'react-instantsearch-dom';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import { Grid, Typography } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/course-cards/constants';
import DomainChips from 'bundles/coursera-plus-landing-page/components/redesign/DomainChips';
import SearchBar from 'bundles/coursera-plus-landing-page/components/redesign/SearchBar';
import type { InputProps as SearchResultsProps } from 'bundles/coursera-plus/components/search/InPageAlgoliaSearchResults';
import InPageAlgoliaSearchResults from 'bundles/coursera-plus/components/search/InPageAlgoliaSearchResults';
import InPageNumberOfResultsSection from 'bundles/coursera-plus/components/search/InPageNumberOfResultsSection';
import { getCopy } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import {
  ALGOLIA_API_KEY,
  ALGOLIA_APP_ID,
  ALGOLIA_PLACEHOLDER_INDEX_NAME,
  ALL_PRODUCTS_INDEX_TYPE,
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING,
  INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME,
  SEARCH_RENDER_DELAY,
} from 'bundles/search-common/constants';
import TrackedIndex from 'bundles/search-common/legacy-algolia/TrackedIndex';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import _t from 'i18n!nls/coursera-plus-landing-page';

const indexTypeToNameMap = () => GrowthDiscoveryExperiments.get(INDEX_TYPE_TO_NAME_EXPERIMENT_PARAMETER_NAME);

const SEARCH_INPUT_LABEL_ID = 'courseraplus-search-title';

const styles = StyleSheet.create({
  containerWrapper: { ...BASE_STYLES.containerWrapper, overflow: 'hidden' },
  container: {
    margin: '0 auto',
    ...BASE_STYLES.container,
    padding: '64px 0',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '48px 0',
    },
  },
  searchContainer: {
    width: '100%',
    backgroundColor: 'white',
  },
  searchDomains: {
    marginTop: '10px',
    textAlign: 'center',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '18px',
    },
  },
  searchTitle: {
    ...BASE_STYLES.title,
    fontSize: '2.125rem !important',
    marginBottom: '24px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      ...BASE_STYLES.titleMobile,
      marginBottom: '22px',
    },
  },
  searchFormWrapper: {
    margin: '0 auto',
  },
  searchResults: {
    marginTop: '48px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '24px',
    },
  },
});

type State = {
  isSearchDelayOn: boolean;
  currentSearchQuery?: string | null;
};

type PropsFromCaller = {
  additionalSearchResultsProps?: Partial<SearchResultsProps>;
  ownsCourseraPlus?: boolean;
  numberOfProductsInPlusOverride?: string;
};

type PropsFromSearchBox = {
  refine: (newValue: string) => void;
};

type PropsFromStores = {
  userAgent: UserAgentInfo;
};

type PropsToComponent = PropsFromCaller & PropsFromSearchBox & PropsFromStores;

export class CourseraPlusSearchContainer extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    _eventData: PropTypes.object,
    router: PropTypes.object.isRequired,
    ais: PropTypes.object.isRequired, // from connectSearchBox
  };

  declare context: LegacyContextType<typeof CourseraPlusSearchContainer.contextTypes>;

  static childContextTypes = {
    router: PropTypes.object.isRequired,
    ais: PropTypes.object.isRequired,
  };

  state = {
    isSearchDelayOn: false,
    currentSearchQuery: '',
  };

  getChildContext() {
    const { ais, router } = this.context;

    return {
      ais,
      router,
    };
  }

  componentDidUpdate(_: $TSFixMe, prevState: $TSFixMe) {
    const { refine } = this.props;
    const { currentSearchQuery } = this.state;
    const { _eventData } = this.context;

    if (prevState.currentSearchQuery !== currentSearchQuery) {
      this.delaySearchResultsRender();
      refine(currentSearchQuery);

      Retracked.trackComponent(
        _eventData,
        {
          searchText: currentSearchQuery,
          page: window.location.pathname,
        },
        'coursera_plus_search_page_query_updated',
        'search'
      );
    }
  }

  delaySearchResultsRender = () => {
    this.setState(() => ({ isSearchDelayOn: true }));
    setTimeout(() => {
      this.setState(() => ({ isSearchDelayOn: false }));
    }, SEARCH_RENDER_DELAY);
  };

  updateSearchQuery = (query: $TSFixMe) => {
    this.setState(() => ({ currentSearchQuery: query }));
  };

  updateSearchQueryToDomain = (domain: $TSFixMe) => {
    this.setState(() => {
      const domainAsSearchQuery = COURSERA_PLUS_POPULAR_DOMAINS_MAPPING.find(
        (currentDomain) => currentDomain.id === domain.toLowerCase()
      )?.name;
      return {
        currentSearchQuery: domainAsSearchQuery,
      };
    });
  };

  render() {
    const indexName = indexTypeToNameMap()[ALL_PRODUCTS_INDEX_TYPE];
    const { userAgent, additionalSearchResultsProps, numberOfProductsInPlusOverride } = this.props;
    const { isSearchDelayOn, currentSearchQuery } = this.state;
    const { numberOfProducts: numberOfProductsInPlus } = getCopy();
    const numberOfProducts = numberOfProductsInPlusOverride ?? numberOfProductsInPlus;
    const sectionTitle = _t('Search #{numberOfProducts} learning programs', { numberOfProducts });

    return (
      <div {...css(styles.containerWrapper)}>
        <Grid className={css(styles.container).className}>
          <div {...css(styles.searchContainer)} data-e2e="search_results">
            <Typography variant="d2semibold" className={css(styles.searchTitle).className} id={SEARCH_INPUT_LABEL_ID}>
              {sectionTitle}
            </Typography>
            <Grid container>
              <Grid item xs={12}>
                <SearchBar
                  currentSearchQuery={currentSearchQuery}
                  updateSearchQuery={this.updateSearchQuery}
                  placeholderText={_t('e.g. Machine Learning')}
                  searchInputLabelId={SEARCH_INPUT_LABEL_ID}
                />
              </Grid>
              <Grid item xs={12} justifyContent="center" className={css(styles.searchDomains).className}>
                <DomainChips
                  domains={COURSERA_PLUS_POPULAR_DOMAINS_MAPPING}
                  updateDomain={this.updateSearchQueryToDomain}
                />
              </Grid>
            </Grid>
            {currentSearchQuery && (
              <TrackedIndex key={ALL_PRODUCTS_INDEX_TYPE} indexName={indexName}>
                <div {...css(styles.searchResults)}>
                  <Configure hitsPerPage={NUMBER_OF_SEARCH_RESULTS} filters="isPartOfCourseraPlus:true" />
                  <div className="tab-contents">
                    <InPageNumberOfResultsSection
                      limitNumberOfResults={NUMBER_OF_SEARCH_RESULTS}
                      currentTab={ALL_PRODUCTS_INDEX_TYPE}
                    />
                    <InPageAlgoliaSearchResults
                      isSearchDelayOn={isSearchDelayOn}
                      userAgent={userAgent}
                      currentTab={ALL_PRODUCTS_INDEX_TYPE}
                      currentSearchQuery={currentSearchQuery}
                      numberOfResults={NUMBER_OF_SEARCH_RESULTS}
                      {...(additionalSearchResultsProps ?? {})}
                    />
                  </div>
                </div>
              </TrackedIndex>
            )}
          </div>
        </Grid>
      </div>
    );
  }
}

// connectSearchBox is an HOC that provides the refine function which updates results
// based on query
const CourseraPlusSearchContainerWithStores = compose<PropsToComponent, PropsFromCaller>(
  connectSearchBox,
  connectToStores<PropsToComponent, PropsFromCaller & PropsFromSearchBox>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      userAgent: ApplicationStore.getUserAgent(),
    })
  )
)(CourseraPlusSearchContainer);

const CourseraPlusSearchContainerWithData = (props: PropsFromCaller) => {
  return (
    // @ts-expect-error TS2769: No overload matches this call. ... Property `children` does not exist on type `IntrinsicAttributes & (IntrinsicClassAttributes<InstantSearch> & Readonly<InstantSearchProps>)`
    <InstantSearch appId={ALGOLIA_APP_ID} apiKey={ALGOLIA_API_KEY} indexName={ALGOLIA_PLACEHOLDER_INDEX_NAME}>
      <CourseraPlusSearchContainerWithStores {...props} />
    </InstantSearch>
  );
};

export default CourseraPlusSearchContainerWithData;
